import type React from 'react';
import { Flex, Tag, Text } from '@nula/core-ui';
import { useUIStore } from '@/stores/use-ui-store';
import type { TranscriptSegmentData } from '@/lib/recordings/parse-transcript';
import type { SpeakerLabel } from './types/types';
import { computeSpeakerStats } from './speaker-stats';
import { speakerColor } from './speaker-color';
import styles from './speaker-chips.module.css';

export interface SpeakerChipsProps {
  segments: TranscriptSegmentData[];
  /** Editable speaker-label overrides — applied so renamed speakers update. */
  speakerLabels?: Record<string, SpeakerLabel>;
  /** Live attendee names by (lowercased) email — labeled speakers show these. */
  attendeeNameByEmail?: Record<string, string>;
  /** The entity a clicked chip's attendees panel should open for. Omitting it
   * falls back to the app-wide active entity — the same mismatch M7 fixed for
   * AttendeesChip: without this, a chip in a non-active split-view pane opens
   * the wrong document's panel. */
  entityId?: string | null;
  /** This recording's audio surface, scoping the panel to its own roster. */
  surfaceId?: string | null;
  className?: string;
}

/** Capitalize the first letter of the display name (leave the rest as-is so
 * multi-word / email-derived names keep their casing). */
function capitalize(name: string): string {
  return name ? name.charAt(0).toUpperCase() + name.slice(1) : name;
}

/**
 * Header row of speaker chips — one per speaker, ordered by talk time
 * descending. Each chip shows a color dot, the resolved (capitalized) speaker
 * name, and their talk-time share. Applies speakerLabels overrides so renaming
 * updates the chip. Clicking a chip opens the Attendees side panel.
 */
export function SpeakerChips({
  segments,
  speakerLabels = {},
  attendeeNameByEmail,
  entityId = null,
  surfaceId = null,
  className,
}: SpeakerChipsProps) {
  const openAttendees = useUIStore((s) => s.setAttendeesPanelOpen);
  const stats = computeSpeakerStats(segments, speakerLabels, attendeeNameByEmail);
  if (stats.length === 0) return null;

  const handleOpen = () => openAttendees(true, entityId, surfaceId);

  return (
    <Flex align="center" gap="xs" wrap className={className ? `${styles.row} ${className}` : styles.row}>
      {stats.map((s) => (
        <Tag
          key={s.speaker}
          role="button"
          tabIndex={0}
          aria-label={`${capitalize(s.speaker)} — open attendees`}
          onClick={handleOpen}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleOpen();
            }
          }}
          className={styles.chip}
        >
          <Flex
            aria-hidden
            className={styles.dot}
            style={{ '--dot-color': speakerColor(s.speaker) } as React.CSSProperties}
          />
          <Text size="xs" className={styles.name} data-testid="speaker-chip-name">
            {capitalize(s.speaker)}
          </Text>
          <Text size="xs" className={styles.pct}>
            {s.pct}%
          </Text>
        </Tag>
      ))}
    </Flex>
  );
}
